import { Clear, FilterAlt } from '@mui/icons-material'
import {
  Autocomplete,
  type AutocompleteRenderInputParams,
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  Menu,
  OutlinedInput,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import type { PlaceNode } from '@ulysses-inc/harami_api_client'
import React, { useState } from 'react'

interface OwnProps {
  deviceName: string
  placeNodeIds: string[]
  placeGroupNodeIds: string[]
  /** 自分が所属する現場のキオスク端末のみ表示するか (デフォルト OFF = 全件表示) */
  isShowOnlyMyPlaces: boolean
  /** 現場名フィルタの選択肢 (Place のみ) */
  placeOptions: PlaceNode[]
  /** 現場グループフィルタの選択肢 (PlaceGroup のみ) */
  placeGroupOptions: PlaceNode[]
  onChangeDeviceName: (deviceName: string) => void
  onChangePlaceNodeIds: (placeNodeIds: string[]) => void
  onChangePlaceGroupNodeIds: (placeGroupNodeIds: string[]) => void
  onChangeShowOnlyMyPlaces: (isShowOnlyMyPlaces: boolean) => void
  onClear: () => void
}

const DevicesFilterDropDown: React.FC<OwnProps> = ({
  deviceName,
  placeNodeIds,
  placeGroupNodeIds,
  isShowOnlyMyPlaces,
  placeOptions,
  placeGroupOptions,
  onChangeDeviceName,
  onChangePlaceNodeIds,
  onChangePlaceGroupNodeIds,
  onChangeShowOnlyMyPlaces,
  onClear,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isOpen = anchorEl !== null

  const isFilterOn =
    deviceName !== '' ||
    placeNodeIds.length > 0 ||
    placeGroupNodeIds.length > 0 ||
    isShowOnlyMyPlaces

  const hoverArea = (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="flex-end">
        <Button onClick={onClear}>クリア</Button>
      </Stack>
      <Stack>
        <Typography variant="subtitle1">キオスク端末名</Typography>
        <OutlinedInput
          fullWidth
          size="small"
          value={deviceName}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            onChangeDeviceName(event.target.value)
          }}
          endAdornment={
            deviceName && (
              <InputAdornment position="end" variant="standard">
                <IconButton
                  aria-label="キオスク端末名をクリア"
                  onClick={() => onChangeDeviceName('')}
                  size="small"
                  color="default"
                >
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }
        />
      </Stack>
      <Stack>
        <Typography variant="subtitle1">
          自分が所属する現場のキオスク端末のみ表示
        </Typography>
        <Switch
          checked={isShowOnlyMyPlaces}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            onChangeShowOnlyMyPlaces(event.target.checked)
          }}
        />
      </Stack>
      <Stack>
        <Typography variant="subtitle1">現場グループ名</Typography>
        <Autocomplete<PlaceNode, true, false, false>
          multiple
          size="small"
          disableCloseOnSelect
          options={placeGroupOptions}
          getOptionLabel={option => option.placeGroup?.name ?? '名称未設定'}
          value={placeGroupNodeIds
            .map(uuid => placeGroupOptions.find(option => option.uuid === uuid))
            .filter((option): option is PlaceNode => option !== undefined)}
          onChange={(_, newValue) => {
            onChangePlaceGroupNodeIds(newValue.map(option => option.uuid))
          }}
          isOptionEqualToValue={(o, v) => o.uuid === v.uuid}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Stack direction="row" alignItems="center" spacing={1} mr={1}>
                <Checkbox size="small" checked={selected} />
                {option.placeGroup?.name ?? '名称未設定'}
              </Stack>
            </li>
          )}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextField {...params} />
          )}
        />
      </Stack>
      <Stack>
        <Typography variant="subtitle1">現場名</Typography>
        <Autocomplete<PlaceNode, true, false, false>
          multiple
          size="small"
          disableCloseOnSelect
          options={placeOptions}
          getOptionLabel={option => option.place?.name ?? '名称未設定'}
          value={placeNodeIds
            .map(uuid => placeOptions.find(option => option.uuid === uuid))
            .filter((option): option is PlaceNode => option !== undefined)}
          onChange={(_, newValue) => {
            onChangePlaceNodeIds(newValue.map(option => option.uuid))
          }}
          isOptionEqualToValue={(o, v) => o.uuid === v.uuid}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Stack direction="row" alignItems="center" spacing={1} mr={1}>
                <Checkbox size="small" checked={selected} />
                {option.place?.name ?? '名称未設定'}
              </Stack>
            </li>
          )}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextField {...params} />
          )}
        />
      </Stack>
    </Stack>
  )

  return (
    <Box>
      <Button
        color={isFilterOn ? 'filtered' : 'secondary'}
        variant="outlined"
        startIcon={<FilterAlt />}
        onClick={({ currentTarget }) => {
          setAnchorEl(currentTarget)
        }}
      >
        絞り込み: {isFilterOn ? 'ON' : 'OFF'}
      </Button>
      <Menu
        id="device-filter-menu"
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => {
          setAnchorEl(null)
        }}
        slotProps={{
          paper: {
            sx: {
              padding: 2,
              width: '100%',
              maxWidth: 400,
            },
          },
          list: {
            sx: {
              padding: 0,
            },
          },
        }}
      >
        {hoverArea}
      </Menu>
    </Box>
  )
}

export default DevicesFilterDropDown
